import React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { theme } from '@weave/theme-original';
import {
  BuildingIcon,
  CaretDownIconSmall,
  CaretUpIconSmall,
  EditIcon,
  ExternalLinkIcon,
  FamilyIcon,
  Heading,
  IconButton,
  IdIcon,
  Modal,
  NakedButton,
  Text,
} from '@weave/design-system';

import { CustomAxios } from '../../../redux/axios';
import { SidebarSyncApp } from '../sidebar-sync-app/sidebar-sync-app.component';
import { LocationModel, DataCenters } from '../../../redux/actions/location';
import { SyncAppMultiCount } from '../../sync-app/sync-app-settings/integrations/sync-app-multi-count.component';
import {
  LocationEdit,
  LocationParentageModal,
  LocationSearchContainer,
} from '../../location';
import {
  accountLinkStyle,
  buildingIconStyle,
  clipboardDiv,
  dropdownContainerStyle,
  iconButtonSvgPrimary,
  iconPointerStyle,
  iconSmall,
  labelFlex,
  labelFlexRight,
  labelInfoDiv,
  mLeftSpacing,
  multiLocationDropdown,
  multiLocationTextStyle,
  sectionIconStyle,
  sidebar,
  statusDot,
} from '../location-sidebar.styles';
import { LocationSearchState } from '../../../redux/actions/location/location-search';
import { randomStringGenerator } from 'helpers/random-string-generator';
import { TcrStatus } from '../location-sidebar.types';
import { AxiosResponse } from 'axios';

interface Props {
  currentLocation: LocationModel | null;
  isMultiOfficeBeta: boolean;
  childrenAccounts: LocationSearchState['locationChildren'];
  setLocation: (locationId) => void;
  allowParentageEdit: boolean;
  hasDatasources: boolean;
  departmentsEnabled: boolean;
}

interface State {
  nameCopied: boolean;
  slugCopied: boolean;
  locationIdCopied: boolean;
  parentIdCopied: boolean;
  isModalOpen: boolean;
  isParentageModalOpen: boolean;
  childIdsCopied: any;
  multiLocationDropdownOpen: boolean;
  parentLocationName: string;
  tcrReferenceIdCopied: boolean;
  tcrStatus: string;
  tcrFetching: boolean;
}

export class LocationSidebar extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      nameCopied: false,
      slugCopied: false,
      locationIdCopied: false,
      parentIdCopied: false,
      isModalOpen: false,
      isParentageModalOpen: false,
      childIdsCopied: {},
      multiLocationDropdownOpen: false,
      parentLocationName: '',
      tcrReferenceIdCopied: false,
      tcrStatus: '',
      tcrFetching: false,
    };
  }

  tcrDDDD = randomStringGenerator({ length: 4 });
  tcrRefVersion = 1;

  tcrMap = {
    TCR_BRAND_IDENTITY_STATUS_SELF_DECLARED: 'Registered',
    TCR_BRAND_IDENTITY_STATUS_VERIFIED: 'Registered',
    TCR_BRAND_IDENTITY_STATUS_VETTED_VERIFIED: 'Registered',
    TCR_BRAND_IDENTITY_STATUS_UNSPECIFIED: 'Unregistered',
    TCR_BRAND_IDENTITY_STATUS_UNVERIFIED: 'Unregistered',
  };

  componentDidMount() {
    this.loadParentLocation();
    this.loadTcrStatus();
  }

  componentDidUpdate(prevProps) {
    if (this.props.currentLocation?.ParentID !== prevProps.currentLocation?.ParentID) {
      this.loadParentLocation();
    }

    if (this.props.currentLocation !== prevProps.currentLocation) {
      this.tcrDDDD = randomStringGenerator({ length: 4 });
    }

    if (
      this.props.currentLocation?.LocationID !== prevProps.currentLocation?.LocationID
    ) {
      this.loadTcrStatus();
    }
  }

  loadParentLocation = async () => {
    if (this.props.currentLocation && this.props.currentLocation.ParentID) {
      try {
        const response = await CustomAxios.get(
          `support/v1/locations/${this.props.currentLocation.ParentID}`
        );
        this.setState({ parentLocationName: response.data.data.Name });
      } catch {
        this.setState({ parentLocationName: '' });
      }
    }
  };

  loadTcrStatus = async () => {
    try {
      if (this.props.currentLocation) {
        this.setState({ tcrFetching: true });
        const response: AxiosResponse<TcrStatus> = await CustomAxios.get(
          `tendlc/coordinator/v1/location-tcr-brand?locationId=${this.props.currentLocation?.LocationID}`
        );
        if (response.data.tcrBrand.identityStatus) {
          this.setState({
            tcrStatus: this.tcrMap[response.data.tcrBrand.identityStatus],
          });
        } else {
          this.setState({ tcrStatus: 'Unregistered' });
          if (this.props.currentLocation && this.props.currentLocation.ParentID) {
            const parentResponse: AxiosResponse<TcrStatus> = await CustomAxios.get(
              `tendlc/coordinator/v1/location-tcr-brand?locationId=${this.props.currentLocation?.ParentID}`
            );
            if (parentResponse.data.tcrBrand.identityStatus) {
              this.setState({
                tcrStatus: this.tcrMap[parentResponse.data.tcrBrand.identityStatus],
              });
            }
          }
        }
      }
      this.setState({ tcrFetching: false });
    } catch {
      this.setState({ tcrStatus: '' });
    }
  };

  toggleModal = () => {
    this.setState({ isModalOpen: !this.state.isModalOpen });
  };

  toggleParentageModal = () => {
    this.setState({ isParentageModalOpen: !this.state.isParentageModalOpen });
  };

  toggleMultiLocationDropdownOpen = () => {
    this.setState({ multiLocationDropdownOpen: !this.state.multiLocationDropdownOpen });
  };

  copiedLocationNameText = () => {
    this.setState({ nameCopied: true });
    setTimeout(() => {
      this.setState({ nameCopied: false });
    }, 3000);
  };

  copiedSlugText = () => {
    this.setState({ slugCopied: true });
    setTimeout(() => {
      this.setState({ slugCopied: false });
    }, 3000);
  };

  copiedLocationIdText = () => {
    this.setState({ locationIdCopied: true });
    setTimeout(() => {
      this.setState({ locationIdCopied: false });
    }, 3000);
  };

  copiedTCRReferenceIdText = () => {
    this.setState({ tcrReferenceIdCopied: true });
    setTimeout(() => {
      this.setState({ tcrReferenceIdCopied: false });
    }, 3000);
  };

  getHomerURL = (dataCenter: DataCenters): string => {
    if (dataCenter === DataCenters.CentralIA) {
      return 'https://homer-us-central1.weavephone.net/';
    } else if (dataCenter === DataCenters.EasternVA) {
      return 'https://homer-us-east4.weavephone.net/';
    } else if (dataCenter === DataCenters.PacificCA) {
      return 'https://homer-us-west2.weavephone.net/';
    }

    return '';
  };

  render() {
    if (!this.props.currentLocation) {
      return null;
    }

    const { LocationID, PhoneTenantID, ParentID, Slug, Name, DataCenter, Active } =
      this.props.currentLocation;
    const childrenAccounts = this.props.childrenAccounts.sort((a, b) =>
      a.Name.localeCompare(b.Name, undefined, { numeric: true, sensitivity: 'base' })
    );
    const homerUrl = this.getHomerURL(DataCenter);

    if (!LocationID) {
      return null;
    }

    return (
      <div css={sidebar}>
        <LocationSearchContainer />

        <div css={labelInfoDiv(theme, 3)}>
          <Heading level={2}>Location Info</Heading>
          {this.props.allowParentageEdit &&
          !this.props.hasDatasources &&
          this.props.childrenAccounts.length === 0 ? (
            <IconButton
              label="Edit Parentage"
              showLabelOnHover={true}
              css={sectionIconStyle}
              onClick={this.toggleParentageModal}
            >
              <IdIcon />
            </IconButton>
          ) : null}
          <IconButton
            label="Edit Location"
            onClick={this.toggleModal}
            css={iconButtonSvgPrimary}
          >
            <EditIcon />
          </IconButton>
        </div>

        <CopyToClipboard text={Name} onCopy={this.copiedLocationNameText}>
          <div css={clipboardDiv} title="Copy to clipboard">
            <BuildingIcon css={buildingIconStyle} />
            <Heading level={2}>{Name}</Heading>
          </div>
        </CopyToClipboard>
        {this.state.nameCopied && (
          <Text color="success" size="medium">
            Copied!
          </Text>
        )}

        <div css={labelInfoDiv(theme)}>
          <Text as="span" size="medium" color="light">
            Departments
          </Text>
          <div css={labelFlexRight}>
            <div
              css={statusDot(theme, this.props.departmentsEnabled ? 'success' : 'error')}
            />
            <Text as="span" size="medium">
              {this.props.departmentsEnabled ? 'Enabled' : 'Disabled'}
            </Text>
          </div>
        </div>

        {this.props.isMultiOfficeBeta && (
          <React.Fragment>
            <hr />

            {ParentID && (
              <>
                <div css={labelInfoDiv(theme, 0)}>
                  <Text as="span" weight="bold" css={multiLocationTextStyle}>
                    Multi-Location
                  </Text>
                  <Text as="span" weight="bold" size="medium">
                    Child
                  </Text>
                </div>

                <div css={labelInfoDiv}>
                  <Text as="span" size="medium" color="light">
                    Parent Account
                  </Text>
                </div>

                <NakedButton
                  css={accountLinkStyle}
                  onClick={() => {
                    this.props.setLocation(ParentID);
                  }}
                >
                  {this.state.parentLocationName || ParentID}
                </NakedButton>
              </>
            )}

            {!ParentID && (
              <React.Fragment>
                {' '}
                <div css={labelInfoDiv(theme, 0)}>
                  <Text weight="bold" css={multiLocationTextStyle}>
                    Multi-Location
                  </Text>
                  <Text as="span" weight="bold" size="medium" css={labelFlexRight}>
                    {childrenAccounts.length} Locations
                  </Text>
                </div>
                <SyncAppMultiCount />
                <div
                  css={dropdownContainerStyle}
                  onClick={() => {
                    this.toggleMultiLocationDropdownOpen();
                  }}
                >
                  <div css={labelInfoDiv(theme, 0)}>
                    <FamilyIcon />

                    <Text as="span" weight="bold" css={mLeftSpacing}>
                      {Name}
                    </Text>
                  </div>
                  {!this.state.multiLocationDropdownOpen && (
                    <CaretDownIconSmall color="light" css={iconPointerStyle} />
                  )}
                  {this.state.multiLocationDropdownOpen && (
                    <CaretUpIconSmall color="light" css={iconPointerStyle} />
                  )}
                </div>
                {this.state.multiLocationDropdownOpen && (
                  <div css={multiLocationDropdown}>
                    <Text size="medium" color="light">
                      Parent Account
                    </Text>

                    <NakedButton
                      css={accountLinkStyle}
                      onClick={() => {
                        this.props.setLocation(LocationID);
                      }}
                    >
                      {Name}
                    </NakedButton>

                    <Text size="medium" color="light">
                      Child Accounts
                    </Text>

                    {childrenAccounts.map((child) => {
                      return (
                        <NakedButton
                          css={accountLinkStyle}
                          onClick={() => {
                            this.props.setLocation(child.LocationID);
                          }}
                          key={child.LocationID}
                        >
                          {child.Name}
                        </NakedButton>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}

        <hr />

        <div css={labelInfoDiv(theme, 0)}>
          <Text as="span" size="medium" color="light">
            Status
          </Text>
          <div css={labelFlexRight}>
            <div css={statusDot(theme, Active ? 'success' : 'error')} />
            <Text as="span" size="medium">
              {Active ? 'Active' : 'Inactive'}
            </Text>
          </div>
        </div>

        <CopyToClipboard text={Slug} onCopy={this.copiedSlugText}>
          <div css={[iconPointerStyle, labelInfoDiv(theme)]}>
            <Text as="span" size="medium" color="light" title="Copy to clipboard">
              Slug
            </Text>
            <Text as="span" size="medium">
              {Slug}
            </Text>
          </div>
        </CopyToClipboard>
        {this.state.slugCopied && (
          <Text color="success" size="medium">
            Copied!
          </Text>
        )}

        <div css={labelInfoDiv}>
          <div css={labelFlex}>
            {homerUrl && (
              <IconButton
                label="Go to Homer portal"
                size="small"
                to={homerUrl}
                rel="noopener noreferrer"
                target="_blank"
                css={mLeftSpacing(theme, -1)}
              >
                <ExternalLinkIcon css={iconSmall} />
              </IconButton>
            )}
            <Text as="span" size="medium" color="light">
              Data Center
            </Text>
          </div>

          <Text as="span" size="medium">
            {DataCenter}
          </Text>
        </div>

        <CopyToClipboard text={LocationID} onCopy={this.copiedLocationIdText}>
          <div css={[iconPointerStyle, labelInfoDiv(theme)]}>
            <Text as="span" size="medium" color="light" title="Copy to clipboard">
              Location ID
            </Text>
            <Text as="span" size="medium">
              {LocationID}
            </Text>
          </div>
        </CopyToClipboard>
        {this.state.locationIdCopied && (
          <Text color="success" size="medium">
            Copied!
          </Text>
        )}
        {this.props.isMultiOfficeBeta && (
          <div css={[labelInfoDiv(theme)]}>
            <Text as="span" size="medium" color="light">
              Phone Tenant ID
            </Text>
            <Text as="span" size="medium">
              {PhoneTenantID || 'None'}
            </Text>
          </div>
        )}

        <CopyToClipboard
          text={`${this.tcrRefVersion}:${ParentID || LocationID}:${this.tcrDDDD}`}
          onCopy={this.copiedTCRReferenceIdText}
        >
          <div css={[iconPointerStyle, labelInfoDiv(theme)]}>
            <Text as="span" size="medium" color="light" title="Copy to clipboard">
              TCR Reference ID
            </Text>
            <Text as="span" size="medium">
              {`${this.tcrRefVersion}:${ParentID || LocationID}:${this.tcrDDDD}`}
            </Text>
          </div>
        </CopyToClipboard>
        {this.state.tcrReferenceIdCopied && (
          <Text color="success" size="medium">
            Copied!
          </Text>
        )}

        {this.state.tcrStatus && !this.state.tcrFetching && (
          <div css={[labelInfoDiv(theme)]}>
            <Text as="span" size="medium" color="light">
              10DLC Status
            </Text>
            <Text as="span" size="medium">
              {`${this.state.tcrStatus}`}
            </Text>
          </div>
        )}
        <hr />

        {!ParentID && <SidebarSyncApp />}

        <Modal show={this.state.isModalOpen} onClose={this.toggleModal} maxWidth={480}>
          <LocationEdit onClose={this.toggleModal} />
        </Modal>

        <Modal
          show={this.state.isParentageModalOpen}
          onClose={this.toggleParentageModal}
          maxWidth={480}
        >
          <LocationParentageModal
            locationID={LocationID}
            parentLocationID={ParentID ?? ''}
            phoneTenantID={PhoneTenantID ?? ''}
            onClose={this.toggleParentageModal}
          />
        </Modal>
      </div>
    );
  }
}
